// ABOUTME: The main dashboard's order-delta panel — a self-contained element (its own query + active-set state
// ABOUTME: + Deltachart + per-series value readout) so it can live on the dashboard and in a BI dashboard cell.
import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { ErrorBoundary } from "react-error-boundary";
import { tokens } from "../../../../theme";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import { useGetOrderDeltaChartQuery } from "../../../../newapi/reports/fulfillment/reportFulfillmentSlice";
import Deltachart from "../deltachart";

const delta_keys = {
    performance_delta: 'performance_delta',
    shipped_orders: 'shipped_orders',
    picked_orders: 'picked_orders',
    new_orders: 'new_orders'
}

// Outer page spacing (px / mt) is intentionally NOT here — the host (page or dashboard cell) decides that.
export const DashboardDeltaPanel = ({ preview }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [activeSet, setActiveSet] = useState(null);
    const [hoverIndex, setHoverIndex] = useState(null);

    const { data, isLoading, isFetching } = useGetOrderDeltaChartQuery(undefined, { skip: preview });
    useEffect(() => {
        if (!isLoading && !isFetching && data?.values) {
            const deltaSet = data.values.find(item => item.name === delta_keys.performance_delta);
            setActiveSet(deltaSet);
            setHoverIndex(data.categories.length - 1);
        }
    }, [data, isLoading, isFetching])

    return (
                <Box
                    sx={{
                        // mx:4,
                        width:1,
                        p:3,
                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        display:'flex',
                        gap:4,
                        height:395,
                        "@media screen and (max-width:47.9375em)" : {
                            flexDirection:"column",
                            minHeight:"730px"
                        }
                    }}
                >
                    <Box
                        sx={{
                            flex:3,
                            display:"flex",
                            flexDirection:"column",
                        }}
                    >
                        <Box
                            sx={{
                                mb:3,
                                display:'flex',
                                justifyContent:"space-between",
                                "@media screen and (max-width:63.9375em)" : {
                                    flexDirection:"column",
                                }
                            }}
                        >
                            <Subheading>
                                {t("order_delta")}
                            </Subheading>
                            {!isLoading && !isFetching && data?.values &&
                                <Box
                                    sx={{
                                        height:32,
                                        p:0.5,
                                        background:colors.grey[100],
                                        display:'flex',
                                        gap:1,
                                        borderRadius:4,
                                        overflow:"hidden",
                                        "@media screen and (max-width:63.9375em)" : {
                                            display:"none"
                                        }
                                    }}
                                >
                                    {data.values.map((val, i) => (
                                        <Box
                                            onClick={() => setActiveSet(val)}
                                            className={cx(undefined, { 'active': activeSet?.name === val.name })}
                                            sx={{
                                                px: 1.5,
                                                font: "Roboto,sans-serif",
                                                fontSize: 14,
                                                color: colors.txt["primary"],
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                maxWidth: "100%",
                                                display:"flex",
                                                alignItems:'center',
                                                borderRadius: 3,
                                                height: 24,
                                                "&.active": {
                                                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                    fontWeight: "bold",
                                                },
                                                "&:hover" : {
                                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],

                                                },
                                                "@media screen and (max-width:63.9375em)" : {
                                                    maxWidth:100,
                                                }
                                            }}
                                        >
                                            {t(val.name)}
                                        </Box>
                                    ))}


                                </Box>
                            }
                        </Box>
                        {data?.categories && activeSet?.name &&
                            <ErrorBoundary
                                FallbackComponent={() => <div>Something went wrong.</div>}
                                onError={(error, info) => console.error(error, info)}
                            >
                                <Deltachart
                                    dataValues={activeSet?.data}
                                    labels={data.categories}
                                    height={300}
                                    onCallback={(index) => {
                                        setHoverIndex(index);
                                    }}
                                />
                            </ErrorBoundary>
                        }
                    </Box>

                    <Box
                        sx={{
                            mt:-1.5,
                            mb:-1.5,
                            mr:-1.5,
                            width:300,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            borderRadius:3,
                            display:'flex',
                            flexDirection:'column',
                            p:1.5,
                            alignItems:"flex-start",

                            "@media screen and (max-width:63.9375em)" : {
                                width:"unset"
                            }
                        }}
                    >
                        <Subheading
                            sx={{
                                pl:1.5,
                                pt:1.5,
                            }}
                        >
                            {t("orders")}
                        </Subheading>
                        {data?.values?.map((set, i) => {
                            const value = set.data[hoverIndex];
                            const isActive = set.name === activeSet?.name;
                            return (
                                isActive ?
                                    <Box
                                        key={`${i}-${hoverIndex}`}
                                        sx={{
                                            width:1,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                            p:1.5,
                                            borderRadius:2,
                                        }}
                                    >
                                        <Text>{t(set.name)}</Text>
                                        <Subheading>
                                            {value}
                                        </Subheading>
                                    </Box>
                                :
                                <Box
                                    onClick={() => setActiveSet(set)}
                                    key={`${i}-${hoverIndex}`}
                                    sx={{
                                        width:1,
                                        p:1.5,
                                        borderRadius:2,
                                        cursor:"pointer",
                                        "&:hover" : {
                                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],

                                        },
                                    }}
                                >
                                    <Text light>{t(set.name)}</Text>
                                    <Subheading>
                                        {value}
                                    </Subheading>
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
    );
};
